import React, { useState } from 'react';
import api from './api';
import { userDisplayName, PROJECT_PRIORITIES, PROJECT_PRIORITY_LABELS, isoToDisplayDate, displayDateToIso } from './helpers';

function ProjectModal({ project, users, onClose, onSaved, onDeleted }) {
  const isEdit = !!project;
  const [form, setForm] = useState({
    name: project?.name || '',
    description: project?.description || '',
    status: project?.status || 'Active',
    priority: project?.priority || 'P2',
    start_date: isoToDisplayDate(project?.start_date),
    end_date: isoToDisplayDate(project?.end_date),
    budget_allocated: project?.budget_allocated || '',
    project_manager_id: project?.project_manager_id || (users[0]?.id ?? ''),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const startIso = displayDateToIso(form.start_date);
    const endIso = displayDateToIso(form.end_date);
    if (form.start_date && startIso === null) {
      setError('Date de début invalide — utilisez le format jj/mm/aaaa.');
      return;
    }
    if (form.end_date && endIso === null) {
      setError('Date de fin invalide — utilisez le format jj/mm/aaaa.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        start_date: startIso || null,
        end_date: endIso || null,
        budget_allocated: parseFloat(form.budget_allocated) || 0,
        project_manager_id: parseInt(form.project_manager_id),
      };
      let res;
      if (isEdit) {
        res = await api.put(`/projects/${project.id}`, payload);
      } else {
        res = await api.post('/projects', payload);
      }
      onSaved(res.data);
    } catch (err) {
      setError("Impossible d'enregistrer le projet.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce projet ?')) return;
    try {
      await api.delete(`/projects/${project.id}`);
      onDeleted(project.id);
    } catch (err) {
      setError('Impossible de supprimer le projet.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>{isEdit ? 'Modifier le projet' : 'Nouveau projet'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {error && <div className="error-message" style={{ marginBottom: 14 }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nom du projet</label>
            <input type="text" value={form.name} onChange={handleChange('name')} required />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea value={form.description} onChange={handleChange('description')} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Statut</label>
              <select value={form.status} onChange={handleChange('status')}>
                <option value="Active">Actif</option>
                <option value="Delayed">En retard</option>
                <option value="Completed">Terminé</option>
              </select>
            </div>
            <div className="form-group">
              <label>Chef de projet</label>
              <select value={form.project_manager_id} onChange={handleChange('project_manager_id')}>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{userDisplayName(u)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Priorité</label>
            <select value={form.priority} onChange={handleChange('priority')}>
              {PROJECT_PRIORITIES.map((p) => (
                <option key={p} value={p}>{PROJECT_PRIORITY_LABELS[p]}</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Date de début</label>
              <input type="text" placeholder="jj/mm/aaaa" value={form.start_date} onChange={handleChange('start_date')} />
            </div>
            <div className="form-group">
              <label>Date de fin</label>
              <input type="text" placeholder="jj/mm/aaaa" value={form.end_date} onChange={handleChange('end_date')} />
            </div>
          </div>

          <div className="form-group">
            <label>Budget alloué (MAD)</label>
            <input type="number" value={form.budget_allocated} onChange={handleChange('budget_allocated')} />
          </div>

          <div className="modal-footer">
            {isEdit && (
              <button type="button" className="btn btn-danger" onClick={handleDelete}>
                Supprimer
              </button>
            )}
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProjectModal;
